import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit, Trash, Download, X, MoreVertical, History, CheckCircle, FileText, Plus } from 'lucide-react';
import { fetchQuotations, deleteQuotation, updateQuotation, createQuotationVersion, updateQuotationStatus, getQuotationVersions, addInvoice } from '../utils/api';
import QuotePreview from '../components/QuotePreview';
import EditQuote from './EditQuote';
import AdhocInvoicePreview from '../components/AdhocInvoicePreview';
import HSNSearchDropdown from '../components/HSNSearchDropdown';
import { useReactToPrint } from 'react-to-print';
import { Select, DatePicker, Input, InputNumber, Table, Card, Tag, Button, Tooltip, Dropdown } from 'antd';
import dayjs from 'dayjs';
import { formatIndianCurrency, numberToWords as toWords } from '../utils/numberToWords';
import { isViewer, getStoredUser } from '../utils/auth';
import { formatInvoiceNumber } from '../utils/invoiceNumber';

function QuoteHistory({ clients = [], config = {}, users = [], currentUser = null }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState('all');

  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Invoice modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceFromQuote, setInvoiceFromQuote] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [financialYear, setFinancialYear] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  const [showShipTo, setShowShipTo] = useState(false);
  const [roundOffEnabled, setRoundOffEnabled] = useState(true);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [editForm, setEditForm] = useState({
    quoteNumber: '',
    quoteDate: '',
    client: '',
    clientName: '',
    billToAddress: '',
    shipToAddress: '',
    reference: '',
    placeOfSupply: '',
    items: []
  });

  const printRef = React.useRef();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Quotation_${selectedQuote?.quoteNumber || 'Quote'}`
  });

  // Separate print ref for invoice preview
  const invoicePrintRef = React.useRef();
  const handleInvoicePrint = useReactToPrint({
    content: () => invoicePrintRef.current,
    documentTitle: `Invoice_${invoiceNumber || 'Invoice'}`
  });

  const load = async () => {
    setLoading(true);
    try {
      const list = await fetchQuotations();
      setRows(list);
    } catch (e) {
      alert(e.message || 'Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Calculate financial year from invoice date
  useEffect(() => {
    try {
      const d = new Date(invoiceDate);
      if (!isNaN(d)) {
        const year = d.getFullYear();
        const nextYear = year + 1;
        setFinancialYear(`${year}-${nextYear.toString().slice(-2)}`);
      }
    } catch (e) {
      console.error('Error setting financial year:', e);
    }
  }, [invoiceDate]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quotation?')) return;
    try {
      await deleteQuotation(id);
      setRows(prev => prev.filter(r => r._id !== id));
    } catch (e) {
      alert(e.message || 'Delete failed');
    }
  };


  const handlePreview = (quote) => {
    setSelectedQuote(quote);
    setShowPreview(true);
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setShowEditModal(true);
  };

  const handleEditSave = async (data) => {
    try {
      // Always create a new version when editing (as per requirements)
      const newVersion = await createQuotationVersion(editingQuote._id, data);
      setRows(prev => [newVersion, ...prev]);
      setShowEditModal(false);
      setEditingQuote(null);
      load(); // Refresh the list
    } catch (error) {
      alert('Failed to save quotation: ' + error.message);
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingQuote(null);
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      const updated = await updateQuotationStatus(quoteId, newStatus);
      setRows(prev => prev.map(r => r._id === quoteId ? updated : r));
    } catch (error) {
      alert('Failed to update status: ' + error.message);
    }
  };

  const handleViewVersions = async (quote) => {
    setSelectedQuote(quote);
    setLoadingVersions(true);
    try {
      const versions = await getQuotationVersions(quote._id);
      setVersionHistory(versions);
      setShowVersionHistory(true);
    } catch (error) {
      alert('Failed to load version history: ' + error.message);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleCreateInvoice = (quote) => {
    // Set the quotation data for invoice creation
    setInvoiceFromQuote(quote);

    // Prefill client data
    setSelectedClient(quote.client?._id || '');

    // Prefill items from quotation
    const prefilledItems = quote.items.map(item => {
      // Calculate amounts if they're not present
      const unitPrice = Number(item.rate || 0);
      const qty = Number(item.qty || 1);
      const netAmount = Number(item.baseAmount || unitPrice * qty);
      const taxPercent = Number(item.taxPercent || 0);
      const taxAmount = Number(item.taxAmount || netAmount * (taxPercent / 100));
      const totalAmount = Number(item.totalAmount || netAmount + taxAmount);

      return {
        hsn: item.hsn || '',
        description: item.description || '',
        unitPrice: unitPrice,
        qty: qty,
        netAmount: netAmount,
        taxPercent: taxPercent,
        taxAmount: taxAmount,
        totalAmount: totalAmount,
        isService: false
      };
    });
    setInvoiceItems(prefilledItems);

    // Reset PO fields (user will enter)
    setPoNumber('');
    setPoDate('');

    // Calculate financial year for invoice number generation
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const nextYear = currentYear + 1;
    const currentFinancialYear = `${currentYear}-${nextYear.toString().slice(-2)}`;

    // Set the financial year immediately
    setFinancialYear(currentFinancialYear);

    // Generate invoice number using configuration
    const newInvoiceNumber = formatInvoiceNumber(config?.nextInvoiceNumber || 1, currentFinancialYear, config);
    setInvoiceNumber(newInvoiceNumber);

    // Open the invoice modal
    setShowInvoiceModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Created': return '#6c757d';
      case 'Approved': return '#28a745';
      case 'Invoiced': return '#007bff';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Created': return <FileText size={16} />;
      case 'Approved': return <CheckCircle size={16} />;
      case 'Invoiced': return <FileText size={16} />;
      default: return <FileText size={16} />;
    }
  };

  const canEdit = (quote) => {
    // All quotations can be edited (they will create new versions)
    return true;
  };

  const canApprove = (quote) => {
    return quote.status === 'Created' && quote.isLatestVersion;
  };

  const canInvoice = (quote) => {
    return quote.status === 'Approved' && quote.isLatestVersion;
  };

  // Invoice functions
  const handleItemChange = (index, field, value) => {
    const newItems = [...invoiceItems];
    newItems[index] = { ...newItems[index], [field]: value };

    if (field === 'unitPrice' || field === 'qty') {
      const unitPrice = Number(newItems[index].unitPrice || 0);
      const qty = Number(newItems[index].qty || 0);
      const netAmount = unitPrice * qty;
      const taxPercent = Number(newItems[index].taxPercent || 0);
      const taxAmount = (netAmount * taxPercent) / 100;
      const totalAmount = netAmount + taxAmount;

      newItems[index].netAmount = Number(netAmount.toFixed(2));
      newItems[index].taxAmount = Number(taxAmount.toFixed(2));
      newItems[index].totalAmount = Number(totalAmount.toFixed(2));
    } else if (field === 'taxPercent') {
      const netAmount = Number(newItems[index].netAmount || 0);
      const taxPercent = Number(value || 0);
      const taxAmount = (netAmount * taxPercent) / 100;
      const totalAmount = netAmount + taxAmount;

      newItems[index].taxAmount = Number(taxAmount.toFixed(2));
      newItems[index].totalAmount = Number(totalAmount.toFixed(2));
    }

    setInvoiceItems(newItems);
  };

  const addInvoiceItem = () => {
    setInvoiceItems([...invoiceItems, {
      hsn: '',
      description: '',
      unitPrice: 0,
      qty: 1,
      netAmount: 0,
      taxPercent: 0,
      taxAmount: 0,
      totalAmount: 0,
      isService: false
    }]);
  };

  const removeInvoiceItem = (index) => {
    if (invoiceItems.length > 1) {
      setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const subtotal = Math.round(invoiceItems.reduce((sum, item) => sum + (item.netAmount || 0), 0) * 100) / 100;
    const totalTax = Math.round(invoiceItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0) * 100) / 100;
    const cgst = Math.round((totalTax / 2) * 100) / 100;
    const sgst = Math.round((totalTax / 2) * 100) / 100;
    const grossTotal = subtotal + totalTax;
    const roundedTotal = Math.round(grossTotal);
    const roundOff = roundOffEnabled ? Math.round((roundedTotal - grossTotal) * 100) / 100 : 0;
    const total = roundOffEnabled ? roundedTotal : Math.round(grossTotal * 100) / 100;

    return {
      subtotal: Number(subtotal.toFixed(2)),
      cgst: Number(cgst.toFixed(2)),
      sgst: Number(sgst.toFixed(2)),
      total: Number(total.toFixed(2)),
      roundOff: Number(roundOff.toFixed(2))
    };
  };

  const numberToWords = (amount) => {
    const safe = Number(amount || 0);
    if (isNaN(safe)) return 'Zero Rupees Only';
    return toWords(safe);
  };

  const handleInvoiceCancel = () => {
    setShowInvoiceModal(false);
    setInvoiceFromQuote(null);
    setInvoiceItems([]);
    setSelectedClient('');
    setInvoiceNumber('');
    setInvoiceDate(dayjs().format('YYYY-MM-DD'));
    setPoNumber('');
    setPoDate('');
    setShowShipTo(false);
  };

  const handleGenerateInvoice = async () => {
    try {
      const selectedClientData = clients.find(c => c._id === selectedClient);
      if (!selectedClientData) {
        alert('Please select a client');
        return;
      }

      if (invoiceItems.length === 0) {
        alert('Please add at least one item');
        return;
      }

      const totals = calculateTotals();

      // Get bank account from config (use first one as fallback)
      const bankAccount = config?.bankAccounts && config.bankAccounts.length > 0
        ? config.bankAccounts[0]
        : null;

      const invoiceData = {
        invoiceNumber,
        invoiceDate,
        financialYear,
        client: selectedClient,
        clientName: selectedClientData.name,
        billToAddress: selectedClientData.billToAddress || '',
        shipToAddress: selectedClientData.shipToAddress || '',
        clientContact: selectedClientData.contact || '',
        // placeOfSupply removed per requirement
        items: invoiceItems.map(item => ({
          hsn: item.hsn || '',
          description: item.description || '',
          qty: Number(item.qty || 1),
          unitPrice: Number(item.unitPrice || 0),
          uom: item.uom || '',
          netAmount: Number(item.netAmount || 0),
          tax: Number(item.taxPercent || 0),
          taxAmount: Number(item.taxAmount || 0),
          totalAmount: Number(item.totalAmount || 0),
          IsServc: item.isService ? 'Y' : 'N'
        })),
        totals: {
          subtotal: totals.subtotal,
          cgst: totals.cgst,
          sgst: totals.sgst,
          roundOff: totals.roundOff,
          total: totals.total
        },
        amountInWords: numberToWords(totals.total),
        poNumber: poNumber || '',
        poDate: poDate || '',
        reference: invoiceFromQuote.quoteNumber,
        relatedQuotation: invoiceFromQuote._id,
        invoiceType: 'adhoc', // Set as ADHOC instead of RENT
        creator: currentUser?.id || currentUser?._id, // Set the creator as current user
        createdBy: currentUser?.name || 'Unknown User', // Set created by name
        bankAccount: bankAccount,
        invoiceCategoryId: null // Quotes don't have invoice categories
      };

      const createdInvoice = await addInvoice(invoiceData);

      // Update quotation status to Invoiced
      await updateQuotationStatus(invoiceFromQuote._id, 'Invoiced');

      // Update the quotation in the list
      setRows(prev => prev.map(row =>
        row._id === invoiceFromQuote._id
          ? { ...row, status: 'Invoiced', relatedInvoice: createdInvoice._id }
          : row
      ));

      alert('Invoice created successfully!');
      handleInvoiceCancel();

    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('Failed to create invoice: ' + error.message);
    }
  };

  const handleEditOld = (quote) => {
    // More comprehensive address handling
    const billToAddress = quote.billToAddress ||
      quote.client?.billToAddress ||
      quote.client?.address ||
      quote.clientAddress ||
      '';

    const shipToAddress = quote.shipToAddress ||
      quote.client?.shipToAddress ||
      quote.client?.address ||
      quote.clientAddress ||
      '';

    setEditingQuote(quote);
    setEditForm({
      quoteNumber: quote.quoteNumber || '',
      quoteDate: quote.quoteDate ? dayjs(quote.quoteDate) : dayjs(),
      client: quote.client?._id || '',
      clientName: quote.clientName || quote.client?.name || '',
      billToAddress: billToAddress,
      shipToAddress: shipToAddress,
      reference: quote.reference || '',
      placeOfSupply: getPlaceOfSupplyValue(quote.placeOfSupply),
      items: quote.items || []
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const updateData = {
        ...editForm,
        quoteDate: editForm.quoteDate.format('YYYY-MM-DD')
      };

      await updateQuotation(editingQuote._id, updateData);
      setShowEditModal(false);
      setEditingQuote(null);
      load(); // Reload the list
      alert('Quotation updated successfully!');
    } catch (error) {
      alert('Failed to update quotation: ' + (error.message || 'Unknown error'));
    }
  };

  const handleFrontendPDF = () => {
    handlePrint();
  };



  // Helper function to convert place of supply code to full format
  const getPlaceOfSupplyValue = (value) => {
    if (!value) return '';

    // If it's already in the correct format, return as is
    if (value.includes('(') && value.includes(')')) {
      return value;
    }

    // If it's just a code, find the matching full format
    const placeOfSupplyOptions = [
      { value: 'Jammu & Kashmir (01)', label: 'Jammu & Kashmir (01)' },
      { value: 'Himachal Pradesh (02)', label: 'Himachal Pradesh (02)' },
      { value: 'Punjab (03)', label: 'Punjab (03)' },
      { value: 'Chandigarh (04)', label: 'Chandigarh (04)' },
      { value: 'Uttarakhand (05)', label: 'Uttarakhand (05)' },
      { value: 'Haryana (06)', label: 'Haryana (06)' },
      { value: 'Delhi (07)', label: 'Delhi (07)' },
      { value: 'Rajasthan (08)', label: 'Rajasthan (08)' },
      { value: 'Uttar Pradesh (09)', label: 'Uttar Pradesh (09)' },
      { value: 'Bihar (10)', label: 'Bihar (10)' },
      { value: 'Sikkim (11)', label: 'Sikkim (11)' },
      { value: 'Arunachal Pradesh (12)', label: 'Arunachal Pradesh (12)' },
      { value: 'Nagaland (13)', label: 'Nagaland (13)' },
      { value: 'Manipur (14)', label: 'Manipur (14)' },
      { value: 'Mizoram (15)', label: 'Mizoram (15)' },
      { value: 'Tripura (16)', label: 'Tripura (16)' },
      { value: 'Meghalaya (17)', label: 'Meghalaya (17)' },
      { value: 'Assam (18)', label: 'Assam (18)' },
      { value: 'West Bengal (19)', label: 'West Bengal (19)' },
      { value: 'Jharkhand (20)', label: 'Jharkhand (20)' },
      { value: 'Odisha (21)', label: 'Odisha (21)' },
      { value: 'Chhattisgarh (22)', label: 'Chhattisgarh (22)' },
      { value: 'Madhya Pradesh (23)', label: 'Madhya Pradesh (23)' },
      { value: 'Gujarat (24)', label: 'Gujarat (24)' },
      { value: 'Dadra and Nagar Haveli and Daman and Diu (26)', label: 'Dadra and Nagar Haveli and Daman and Diu (26)' },
      { value: 'Maharashtra (27)', label: 'Maharashtra (27)' },
      { value: 'Karnataka (29)', label: 'Karnataka (29)' },
      { value: 'Goa (30)', label: 'Goa (30)' },
      { value: 'Lakshadweep (31)', label: 'Lakshadweep (31)' },
      { value: 'Kerala (32)', label: 'Kerala (32)' },
      { value: 'Tamil Nadu (33)', label: 'Tamil Nadu (33)' },
      { value: 'Puducherry (34)', label: 'Puducherry (34)' },
      { value: 'Andaman & Nicobar Islands (35)', label: 'Andaman & Nicobar Islands (35)' },
      { value: 'Telangana (36)', label: 'Telangana (36)' },
      { value: 'Andhra Pradesh (New) (37)', label: 'Andhra Pradesh (New) (37)' },
      { value: 'Ladakh (38)', label: 'Ladakh (38)' },
      { value: 'Other Territory (97)', label: 'Other Territory (97)' },
      { value: 'Center Jurisdiction (99)', label: 'Center Jurisdiction (99)' }
    ];

    const found = placeOfSupplyOptions.find(option => option.value.includes(`(${value})`));
    return found ? found.value : value;
  };

  const activeUser = currentUser || getStoredUser();

  const uniqueCreators = useMemo(() => {
    const creatorMap = new Map();
    rows.forEach(q => {
      if (q.creator && typeof q.creator === 'object' && (q.creator._id || q.creator.id)) {
        const id = String(q.creator._id || q.creator.id);
        const name = q.creator.name || q.creator.email || 'Unnamed User';
        if (!creatorMap.has(id)) {
          creatorMap.set(id, { id, name });
        }
      }
    });
    if (activeUser && (activeUser._id || activeUser.id)) {
      const cId = String(activeUser._id || activeUser.id);
      if (!creatorMap.has(cId) && activeUser.name) {
        creatorMap.set(cId, { id: cId, name: activeUser.name });
      }
    }
    return Array.from(creatorMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, activeUser]);

  const hasUnassignedQuotes = useMemo(() => {
    return rows.some(q => !q.creator);
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (selectedCreator === 'all') return rows;
    const activeUserId = activeUser?._id || activeUser?.id;
    return rows.filter(q => {
      const qCreatorId = q.creator
        ? (typeof q.creator === 'object' ? (q.creator._id || q.creator.id) : q.creator)
        : null;
      const qCreatorIdStr = qCreatorId ? String(qCreatorId) : null;
      if (selectedCreator === 'my_quotes') {
        return activeUserId && qCreatorIdStr === String(activeUserId);
      }
      if (selectedCreator === 'unassigned') {
        return !qCreatorIdStr;
      }
      return qCreatorIdStr === String(selectedCreator);
    });
  }, [rows, selectedCreator, activeUser]);

  const columns = [
    {
      title: 'SlNo',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Quote Number',
      dataIndex: 'quoteNumber',
      key: 'quoteNumber',
      width: 150,
      render: (text, record) => (
        <a onClick={() => navigate(`/quotes/${record._id}`)} style={{ fontWeight: 500 }}>
          {text}
        </a>
      ),
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 100,
      render: (version, record) => (
        <Tag color={record.isLatestVersion ? 'blue' : 'default'}>
          v{version || 1}{record.isLatestVersion && ' (Latest)'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        let color = 'default';
        let icon = <FileText size={14} />;

        if (status === 'Approved') {
          color = 'success';
          icon = <CheckCircle size={14} />;
        } else if (status === 'Invoiced') {
          color = 'processing';
        }

        return (
          <Tag color={color} icon={icon} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {status || 'Created'}
          </Tag>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'quoteDate',
      key: 'quoteDate',
      width: 120,
      render: (date) => date ? new Date(date).toLocaleDateString('en-IN') : '',
    },
    {
      title: 'Client',
      dataIndex: 'clientName',
      key: 'clientName',
      render: (text, record) => text || record.client?.name || '',
    },
    {
      title: 'Total',
      dataIndex: 'totals',
      key: 'total',
      width: 120,
      align: 'right',
      render: (totals) => `₹${(totals?.total || 0).toFixed(2)}`,
    },
    {
      title: 'Created By',
      dataIndex: ['creator', 'name'],
      key: 'creator',
      width: 120,
      render: (text) => text || 'N/A',
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 120,
      render: (text) => text || 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => {
        const canMutate = !isViewer();
        const items = [
          {
            key: 'download',
            label: 'Download PDF',
            icon: <Download size={14} style={{ color: '#2563eb' }} />,
            onClick: () => {
              setSelectedQuote(record);
              setShowPreview(true);
              setTimeout(() => handleFrontendPDF(), 100);
            },
          },
        ];

        if (canMutate) {
          items.unshift({
            key: 'edit',
            label: 'Edit Quote',
            icon: <Edit size={14} style={{ color: '#f59e0b' }} />,
            onClick: () => handleEdit(record),
          });
        }

        if (canMutate && canApprove(record)) {
          items.push({
            key: 'approve',
            label: 'Approve Quote',
            icon: <CheckCircle size={14} style={{ color: '#059669' }} />,
            onClick: () => handleStatusChange(record._id, 'Approved'),
          });
        }

        if (canMutate && canInvoice(record)) {
          items.push({
            key: 'invoice',
            label: 'Create Invoice',
            icon: <FileText size={14} style={{ color: '#2563eb' }} />,
            onClick: () => handleCreateInvoice(record),
          });
        }

        if (canMutate) {
          items.push({
            type: 'divider',
          });

          items.push({
            key: 'delete',
            label: 'Delete',
            danger: true,
            icon: <Trash size={14} />,
            onClick: () => handleDelete(record._id),
          });
        }

        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button type="text" icon={<MoreVertical size={16} />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h1>Quotation History</h1>
          <p>List of saved quotations with PDF download.</p>
        </div>
        <div className="page-actions">
          <Select
            value={selectedCreator}
            onChange={(val) => setSelectedCreator(val)}
            style={{ width: 180 }}
            options={[
              { value: 'all', label: 'All Creators' },
              ...(activeUser && (activeUser._id || activeUser.id) ? [{ value: 'my_quotes', label: 'My Quotes' }] : []),
              ...uniqueCreators.map(c => ({ value: c.id, label: c.name })),
              ...(hasUnassignedQuotes ? [{ value: 'unassigned', label: 'Unassigned' }] : [])
            ]}
          />
        </div>
      </div>

      <Card loading={loading}>
        <Table
          dataSource={filteredRows}
          columns={columns}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* Preview Modal */}
      {showPreview && selectedQuote && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e6ecf1',
              backgroundColor: '#f8f9fa'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Quote Preview - {selectedQuote.quoteNumber}</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-primary" onClick={handlePrint}>Print Quote</button>
                <button
                  className="btn"
                  style={{ background: 'none', border: 'none', fontSize: '22px', color: '#888', cursor: 'pointer', padding: '6px 8px' }}
                  onClick={() => setShowPreview(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div style={{ padding: '20px 30px 30px 30px' }}>
              <QuotePreview
                client={selectedQuote.client}
                clientName={selectedQuote.clientName}
                billToAddress={selectedQuote.billToAddress}
                shipToAddress={selectedQuote.shipToAddress}
                clientContact={selectedQuote.clientContact}
                quoteNumber={selectedQuote.quoteNumber}
                quoteDate={selectedQuote.quoteDate}
                reference={selectedQuote.reference}
                placeOfSupply={selectedQuote.placeOfSupply}
                items={selectedQuote.items}
                totals={selectedQuote.totals}
                amountInWords={selectedQuote.amountInWords}
                printRef={printRef}
                handlePrint={handlePrint}
                config={config}
              />
            </div>
          </div>
        </div>
      )}

      {/* Legacy Edit Modal - replaced by EditQuote component */}
      {false && showEditModal && editingQuote && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e6ecf1',
              backgroundColor: '#f8f9fa'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Edit Quotation - {editingQuote.quoteNumber}</h3>
              <button
                className="btn"
                style={{ background: 'none', border: 'none', fontSize: '22px', color: '#888', cursor: 'pointer', padding: '6px 8px' }}
                onClick={() => setShowEditModal(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Quote Number</label>
                  <Input
                    value={editForm.quoteNumber}
                    onChange={(e) => setEditForm({ ...editForm, quoteNumber: e.target.value })}
                    placeholder="Quote Number"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Quote Date</label>
                  <DatePicker
                    value={editForm.quoteDate}
                    onChange={(date) => setEditForm({ ...editForm, quoteDate: date })}
                    style={{ width: '100%' }}
                    format="DD-MMM-YYYY"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                {editForm.client ? (
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Client</label>
                    <Select
                      value={editForm.client}
                      onChange={(value) => setEditForm({ ...editForm, client: value })}
                      style={{ width: '100%' }}
                      placeholder="Select Client"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={clients.map(client => ({
                        value: client._id,
                        label: client.name || client.registeredName
                      }))}
                    />
                  </div>
                ) : (
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Client Name (Manual Entry)</label>
                    <Input
                      value={editForm.clientName}
                      onChange={(e) => setEditForm({ ...editForm, clientName: e.target.value })}
                      placeholder="Enter client name manually"
                    />
                  </div>
                )}
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Reference</label>
                  <Select
                    value={editForm.reference}
                    onChange={(value) => setEditForm({ ...editForm, reference: value })}
                    style={{ width: '100%' }}
                    placeholder="Select Reference"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={users.map(user => ({
                      value: user.name,
                      label: user.name
                    }))}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Place of Supply</label>
                <Select
                  value={editForm.placeOfSupply}
                  onChange={(value) => setEditForm({ ...editForm, placeOfSupply: value })}
                  style={{ width: '100%' }}
                  placeholder="Select Place of Supply"
                  options={[
                    { value: 'Jammu & Kashmir (01)', label: 'Jammu & Kashmir (01)' },
                    { value: 'Himachal Pradesh (02)', label: 'Himachal Pradesh (02)' },
                    { value: 'Punjab (03)', label: 'Punjab (03)' },
                    { value: 'Chandigarh (04)', label: 'Chandigarh (04)' },
                    { value: 'Uttarakhand (05)', label: 'Uttarakhand (05)' },
                    { value: 'Haryana (06)', label: 'Haryana (06)' },
                    { value: 'Delhi (07)', label: 'Delhi (07)' },
                    { value: 'Rajasthan (08)', label: 'Rajasthan (08)' },
                    { value: 'Uttar Pradesh (09)', label: 'Uttar Pradesh (09)' },
                    { value: 'Bihar (10)', label: 'Bihar (10)' },
                    { value: 'Sikkim (11)', label: 'Sikkim (11)' },
                    { value: 'Arunachal Pradesh (12)', label: 'Arunachal Pradesh (12)' },
                    { value: 'Nagaland (13)', label: 'Nagaland (13)' },
                    { value: 'Manipur (14)', label: 'Manipur (14)' },
                    { value: 'Mizoram (15)', label: 'Mizoram (15)' },
                    { value: 'Tripura (16)', label: 'Tripura (16)' },
                    { value: 'Meghalaya (17)', label: 'Meghalaya (17)' },
                    { value: 'Assam (18)', label: 'Assam (18)' },
                    { value: 'West Bengal (19)', label: 'West Bengal (19)' },
                    { value: 'Jharkhand (20)', label: 'Jharkhand (20)' },
                    { value: 'Odisha (21)', label: 'Odisha (21)' },
                    { value: 'Chhattisgarh (22)', label: 'Chhattisgarh (22)' },
                    { value: 'Madhya Pradesh (23)', label: 'Madhya Pradesh (23)' },
                    { value: 'Gujarat (24)', label: 'Gujarat (24)' },
                    { value: 'Dadra and Nagar Haveli and Daman and Diu (26)', label: 'Dadra and Nagar Haveli and Daman and Diu (26)' },
                    { value: 'Maharashtra (27)', label: 'Maharashtra (27)' },
                    { value: 'Karnataka (29)', label: 'Karnataka (29)' },
                    { value: 'Goa (30)', label: 'Goa (30)' },
                    { value: 'Lakshadweep (31)', label: 'Lakshadweep (31)' },
                    { value: 'Kerala (32)', label: 'Kerala (32)' },
                    { value: 'Tamil Nadu (33)', label: 'Tamil Nadu (33)' },
                    { value: 'Puducherry (34)', label: 'Puducherry (34)' },
                    { value: 'Andaman & Nicobar Islands (35)', label: 'Andaman & Nicobar Islands (35)' },
                    { value: 'Telangana (36)', label: 'Telangana (36)' },
                    { value: 'Andhra Pradesh (New) (37)', label: 'Andhra Pradesh (New) (37)' },
                    { value: 'Ladakh (38)', label: 'Ladakh (38)' },
                    { value: 'Other Territory (97)', label: 'Other Territory (97)' },
                    { value: 'Center Jurisdiction (99)', label: 'Center Jurisdiction (99)' }
                  ]}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Bill To Address</label>
                  <Input.TextArea
                    value={editForm.billToAddress || ''}
                    onChange={(e) => setEditForm({ ...editForm, billToAddress: e.target.value })}
                    placeholder="Bill To Address"
                    rows={3}
                  />
                  {editForm.billToAddress && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Current: {editForm.billToAddress}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Ship To Address</label>
                  <Input.TextArea
                    value={editForm.shipToAddress || ''}
                    onChange={(e) => setEditForm({ ...editForm, shipToAddress: e.target.value })}
                    placeholder="Ship To Address"
                    rows={3}
                  />
                  {editForm.shipToAddress && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Current: {editForm.shipToAddress}
                    </div>
                  )}
                </div>
              </div>

              {/* Line Items Section */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>Line Items</h4>
                <div style={{ border: '1px solid #e6ecf1', borderRadius: '6px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e6ecf1', fontSize: '12px' }}>Sr No</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e6ecf1', fontSize: '12px' }}>Description</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e6ecf1', fontSize: '12px' }}>HSN/SAC</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e6ecf1', fontSize: '12px' }}>Qty</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e6ecf1', fontSize: '12px' }}>Rate</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e6ecf1', fontSize: '12px' }}>Base Amt</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e6ecf1', fontSize: '12px' }}>Tax %</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e6ecf1', fontSize: '12px' }}>Tax Amt</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e6ecf1', fontSize: '12px' }}>Total Amt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(editForm.items || []).map((item, index) => (
                        <tr key={index} style={{ borderBottom: index < (editForm.items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>
                          <td style={{ padding: '8px', borderBottom: index < (editForm.items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>{index + 1}</td>
                          <td style={{ padding: '8px', borderBottom: index < (editForm.items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>
                            <div style={{ position: 'relative' }}>
                              <Input
                                value={item.description || ''}
                                onChange={(e) => {
                                  const newItems = [...(editForm.items || [])];
                                  newItems[index] = { ...newItems[index], description: e.target.value };
                                  setEditForm({ ...editForm, items: newItems });
                                }}
                                placeholder="Description"
                                style={{ border: 'none', boxShadow: 'none', paddingRight: '40px' }}
                              />
                              <label
                                htmlFor={`edit-image-upload-${index}`}
                                style={{
                                  position: 'absolute',
                                  right: '8px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  color: '#666',
                                  background: 'none',
                                  border: 'none',
                                  padding: '4px',
                                  borderLeft: '2px solid #e6ecf0',
                                  paddingLeft: '8px',
                                  backgroundColor: '#fff'
                                }}
                                title="Upload Image"
                              >
                                📷
                              </label>
                              <input
                                id={`edit-image-upload-${index}`}
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onload = (event) => {
                                      const newItems = [...(editForm.items || [])];
                                      newItems[index] = {
                                        ...newItems[index],
                                        imageBase64: event.target.result,
                                        imageName: file.name
                                      };
                                      setEditForm({ ...editForm, items: newItems });
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                                style={{ display: 'none' }}
                              />
                            </div>
                            {item.imageBase64 && (
                              <div style={{ marginTop: '8px', padding: '8px', border: '1px solid #e6ecf1', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                  <span style={{ fontSize: '12px', color: '#2563eb', fontWeight: '500' }}>
                                    📎 {item.imageName || 'Image'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newItems = [...(editForm.items || [])];
                                      newItems[index] = {
                                        ...newItems[index],
                                        imageBase64: null,
                                        imageName: null
                                      };
                                      setEditForm({ ...editForm, items: newItems });
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#dc3545',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      padding: '2px 4px'
                                    }}
                                    title="Remove Image"
                                  >
                                    ✕
                                  </button>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <img
                                    src={item.imageBase64}
                                    alt="Preview"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '80px',
                                      objectFit: 'contain',
                                      border: '1px solid #e6ecf1',
                                      borderRadius: '4px',
                                      backgroundColor: 'white'
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '8px', borderBottom: index < (editForm.items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>
                            <Input
                              value={item.hsn || ''}
                              onChange={(e) => {
                                const newItems = [...(editForm.items || [])];
                                newItems[index] = { ...newItems[index], hsn: e.target.value };
                                setEditForm({ ...editForm, items: newItems });
                              }}
                              placeholder="HSN/SAC"
                              style={{ border: 'none', boxShadow: 'none' }}
                            />
                          </td>
                          <td style={{ padding: '8px', borderBottom: index < (editForm.items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>
                            <InputNumber
                              value={item.qty || 0}
                              onChange={(value) => {
                                const newItems = [...(editForm.items || [])];
                                newItems[index] = { ...newItems[index], qty: value };
                                setEditForm({ ...editForm, items: newItems });
                              }}
                              placeholder="Qty"
                              style={{ border: 'none', boxShadow: 'none', width: '100%' }}
                              min={0}
                            />
                          </td>
                          <td style={{ padding: '8px', borderBottom: index < (editForm.items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>
                            <InputNumber
                              value={item.rate || 0}
                              onChange={(value) => {
                                const newItems = [...(editForm.items || [])];
                                newItems[index] = { ...newItems[index], rate: value };
                                setEditForm({ ...editForm, items: newItems });
                              }}
                              placeholder="Rate"
                              style={{ border: 'none', boxShadow: 'none', width: '100%' }}
                              min={0}
                            />
                          </td>
                          <td style={{ padding: '8px', borderBottom: index < (editForm.items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>
                            <InputNumber
                              value={item.baseAmount || 0}
                              onChange={(value) => {
                                const newItems = [...(editForm.items || [])];
                                newItems[index] = { ...newItems[index], baseAmount: value };
                                setEditForm({ ...editForm, items: newItems });
                              }}
                              placeholder="Base Amt"
                              style={{ border: 'none', boxShadow: 'none', width: '100%' }}
                              min={0}
                            />
                          </td>
                          <td style={{ padding: '8px', borderBottom: index < (editForm.items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>
                            <InputNumber
                              value={item.taxPercent || 0}
                              onChange={(value) => {
                                const newItems = [...(editForm.items || [])];
                                newItems[index] = { ...newItems[index], taxPercent: value };
                                setEditForm({ ...editForm, items: newItems });
                              }}
                              placeholder="Tax %"
                              style={{ border: 'none', boxShadow: 'none', width: '100%' }}
                              min={0}
                              max={100}
                            />
                          </td>
                          <td style={{ padding: '8px', borderBottom: index < (editForm.items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>
                            <InputNumber
                              value={item.taxAmount || 0}
                              onChange={(value) => {
                                const newItems = [...(editForm.items || [])];
                                newItems[index] = { ...newItems[index], taxAmount: value };
                                setEditForm({ ...editForm, items: newItems });
                              }}
                              placeholder="Tax Amt"
                              style={{ border: 'none', boxShadow: 'none', width: '100%' }}
                              min={0}
                            />
                          </td>
                          <td style={{ padding: '8px', borderBottom: index < (editForm.items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>
                            <InputNumber
                              value={item.totalAmount || 0}
                              onChange={(value) => {
                                const newItems = [...(editForm.items || [])];
                                newItems[index] = { ...newItems[index], totalAmount: value };
                                setEditForm({ ...editForm, items: newItems });
                              }}
                              placeholder="Total Amt"
                              style={{ border: 'none', boxShadow: 'none', width: '100%' }}
                              min={0}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Quote Modal */}
      {showEditModal && editingQuote && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <EditQuote
              quotation={editingQuote}
              clients={clients}
              config={config}
              user={{}}
              onSave={handleEditSave}
              onCancel={handleEditCancel}
            />
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {showVersionHistory && selectedQuote && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '1000px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e6ecf1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3>Version History - {selectedQuote.quoteNumber}</h3>
              <button
                onClick={() => setShowVersionHistory(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {loadingVersions ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>Loading versions...</div>
              ) : (
                <div style={{ overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Version</th>
                        <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Status</th>
                        <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Created</th>
                        <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Created By</th>
                        <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Total</th>
                        <th style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'left' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versionHistory.map((version, index) => (
                        <tr key={version._id}>
                          <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                            <span style={{
                              background: version.isLatestVersion ? '#e3f2fd' : '#f5f5f5',
                              color: version.isLatestVersion ? '#1976d2' : '#666',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              v{version.version || 1}
                              {version.isLatestVersion && ' (Latest)'}
                            </span>
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                            <span style={{
                              background: getStatusColor(version.status),
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              {getStatusIcon(version.status)}
                              {version.status || 'Created'}
                            </span>
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                            {version.createdAt ? new Date(version.createdAt).toLocaleDateString('en-IN') : ''}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                            {version.creator?.name || 'N/A'}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                            ₹{(version.totals?.total || 0).toFixed(2)}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '12px' }}>
                            <button
                              onClick={() => {
                                setSelectedQuote(version);
                                setShowVersionHistory(false);
                                setShowPreview(true);
                              }}
                              style={{
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Creation Modal */}
      {showInvoiceModal && invoiceFromQuote && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e6ecf1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3>Create Invoice from Quotation - {invoiceFromQuote.quoteNumber}</h3>
              <button
                onClick={handleInvoiceCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Invoice Header */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Invoice Number</label>
                  <Input
                    value={invoiceNumber}
                    disabled
                    style={{ marginBottom: '10px', width: '100%', backgroundColor: '#f5f5f5' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Invoice Date</label>
                  <DatePicker
                    value={dayjs(invoiceDate)}
                    onChange={date => setInvoiceDate(date.format('YYYY-MM-DD'))}
                    style={{ width: '100%', marginBottom: '10px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Financial Year</label>
                  <Input
                    value={financialYear}
                    readOnly
                    style={{ marginBottom: '10px', width: '100%', backgroundColor: '#f5f5f5' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Place of Supply</label>
                  <Input
                    value={getPlaceOfSupplyValue(invoiceFromQuote?.placeOfSupply) || ''}
                    readOnly
                    style={{ marginBottom: '10px', width: '100%', backgroundColor: '#f5f5f5' }}
                  />
                </div>
              </div>

              {/* Client, PO and Reference Details */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', alignItems: 'end' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Select Client</label>
                    <Select
                      value={selectedClient}
                      onChange={setSelectedClient}
                      options={clients.map(client => ({ label: client.name, value: client._id }))}
                      style={{ width: '100%', marginBottom: '10px' }}
                      placeholder="Select Client"
                      showSearch
                      filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>PO Number</label>
                    <Input
                      value={poNumber}
                      onChange={e => setPoNumber(e.target.value)}
                      placeholder="Enter PO Number"
                      style={{ marginBottom: '10px', width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>PO Date</label>
                    <DatePicker
                      value={poDate ? dayjs(poDate) : null}
                      onChange={(d) => setPoDate(d ? d.format('YYYY-MM-DD') : '')}
                      style={{ width: '100%', marginBottom: '10px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Reference Quote Number</label>
                    <Input
                      value={invoiceFromQuote?.quoteNumber || ''}
                      readOnly
                      style={{ marginBottom: '10px', backgroundColor: '#f5f5f5', width: '100%' }}
                    />
                  </div>
                </div>
              </div>

              {/* Invoice Items */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4>Invoice Items</h4>
                  <button className="btn btn-primary" onClick={addInvoiceItem}>
                    <Plus size={16} style={{ marginRight: '8px' }} />
                    Add Item
                  </button>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Sr No</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>HSN/SAC</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Description</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Unit Price</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Qty</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Net Amount</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Tax %</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Tax Amount</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Total Amount</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceItems.map((item, index) => (
                        <tr key={index}>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>{index + 1}</td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            <HSNSearchDropdown
                              value={item.hsn}
                              onChange={(value) => handleItemChange(index, 'hsn', value)}
                              placeholder="HSN/SAC"
                            />
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            <Input
                              value={item.description}
                              onChange={e => handleItemChange(index, 'description', e.target.value)}
                              placeholder="Description"
                            />
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            <InputNumber
                              value={item.unitPrice}
                              onChange={value => handleItemChange(index, 'unitPrice', value)}
                              style={{ width: '100%' }}
                            />
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            <InputNumber
                              value={item.qty}
                              onChange={value => handleItemChange(index, 'qty', value)}
                              style={{ width: '100%' }}
                            />
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            <InputNumber
                              value={item.netAmount}
                              readOnly
                              style={{ width: '100%' }}
                            />
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            <InputNumber
                              value={item.taxPercent}
                              onChange={value => handleItemChange(index, 'taxPercent', value)}
                              style={{ width: '100%' }}
                            />
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            <InputNumber
                              value={item.taxAmount}
                              readOnly
                              style={{ width: '100%' }}
                            />
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            <InputNumber
                              value={item.totalAmount}
                              readOnly
                              style={{ width: '100%' }}
                            />
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            <button
                              className="btn btn-danger"
                              onClick={() => removeInvoiceItem(index)}
                              disabled={invoiceItems.length === 1}
                            >
                              <Trash size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Invoice Summary */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: 12 }}>Invoice Summary</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', alignItems: 'end' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Subtotal</label>
                    <Input
                      value={formatIndianCurrency(calculateTotals().subtotal)}
                      readOnly
                      style={{ marginBottom: '10px', width: '100%', backgroundColor: '#f5f5f5' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>CGST</label>
                    <Input
                      value={formatIndianCurrency(calculateTotals().cgst)}
                      readOnly
                      style={{ marginBottom: '10px', width: '100%', backgroundColor: '#f5f5f5' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>SGST</label>
                    <Input
                      value={formatIndianCurrency(calculateTotals().sgst)}
                      readOnly
                      style={{ marginBottom: '10px', width: '100%', backgroundColor: '#f5f5f5' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Total</label>
                    <Input
                      value={formatIndianCurrency(calculateTotals().total)}
                      readOnly
                      style={{ marginBottom: '10px', width: '100%', backgroundColor: '#f0f9ff', fontWeight: 600 }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 8 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Amount in Words</label>
                  <Input
                    value={numberToWords(calculateTotals().total)}
                    readOnly
                    style={{ marginBottom: '10px', width: '100%', backgroundColor: '#f5f5f5' }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleInvoiceCancel}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-info"
                  onClick={() => setShowInvoicePreview(true)}
                >
                  Preview Invoice
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleGenerateInvoice}
                >
                  Generate Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Preview Modal */}
      {showInvoicePreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e6ecf1',
              backgroundColor: '#f8f9fa'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Invoice Preview</h3>
              <button
                className="btn"
                style={{ background: 'none', border: 'none', fontSize: '22px', color: '#888', cursor: 'pointer', padding: '6px 8px' }}
                onClick={() => setShowInvoicePreview(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <AdhocInvoicePreview
                client={clients.find(c => c._id === selectedClient)}
                invoiceNumber={invoiceNumber}
                invoiceDate={invoiceDate}
                financialYear={financialYear}
                items={invoiceItems}
                subtotal={calculateTotals().subtotal}
                cgst={calculateTotals().cgst}
                sgst={calculateTotals().sgst}
                roundOff={calculateTotals().roundOff}
                total={calculateTotals().total}
                amountInWords={numberToWords(calculateTotals().total)}
                poNumber={poNumber}
                poDate={poDate}
                referenceQuoteNumber={invoiceFromQuote?.quoteNumber}
                placeOfSupply={invoiceFromQuote?.placeOfSupply || ''}
                config={config}
                printRef={invoicePrintRef}
                handlePrint={handleInvoicePrint}
              />

              {/* Preview Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e6ecf1' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowInvoicePreview(false)}
                >
                  Back to Edit
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleGenerateInvoice}
                >
                  Generate Invoice
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleInvoicePrint}
                >
                  Print Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuoteHistory;


